import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { FileText, CreditCard, User, LogOut, CheckCircle, Clock, XCircle, Key, Download, Wifi, DollarSign, Calendar, Printer, Building2, X, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CustomerWiFiManagement from '../components/CustomerWiFiManagement';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Tab state for dashboard sections
  const [activeTab, setActiveTab] = useState('billing'); // 'billing' or 'wifi'
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedBank, setSelectedBank] = useState('BRI');
  const [vaResult, setVaResult] = useState(null);
  
  // Available VA Banks
  const availableBanks = [
    { code: 'BRI', name: 'Bank BRI', color: 'bg-blue-600' },
    { code: 'BNI', name: 'Bank BNI', color: 'bg-orange-500' },
    { code: 'MANDIRI', name: 'Bank Mandiri', color: 'bg-blue-800' },
    { code: 'PERMATA', name: 'Bank Permata', color: 'bg-green-600' },
    { code: 'BCA', name: 'Bank BCA', color: 'bg-blue-500' },
    { code: 'CIMB', name: 'Bank CIMB Niaga', color: 'bg-red-600' },
    { code: 'BSI', name: 'Bank Syariah Indonesia', color: 'bg-teal-600' },
  ];

  useEffect(() => {
    // Add retry logic to handle potential race condition with localStorage
    let retryCount = 0;
    const maxRetries = 3;
    
    const checkAuth = () => {
      const token = localStorage.getItem('customer_token');
      const customerData = localStorage.getItem('customer_data');

      if (!token || !customerData) {
        // Retry a few times before redirecting, in case localStorage is being written
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkAuth, 100);
          return;
        }
        // After retries, redirect to login
        navigate('/customer-login');
        return;
      }

      setCustomer(JSON.parse(customerData));
      fetchInvoices(token);
    };
    
    checkAuth();
  }, [navigate]);

  const fetchInvoices = async (token) => {
    try {
      // Fetch invoices and dashboard stats in parallel - using V2 MySQL endpoints
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/v2/customer/invoices`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/v2/customer/dashboard-stats`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);

      if (invoicesRes.data.success) {
        setInvoices(invoicesRes.data.data);
      }
      
      if (statsRes.data.success) {
        setDashboardStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Sesi Anda telah berakhir. Silakan login kembali.');
        handleLogout();
      } else {
        toast.error('Gagal memuat data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (invoice) => {
    // Redirect to payment confirmation page with select mode enabled
    navigate(`/payment/confirmation?invoice_id=${invoice.id}&select=true`);
  };
  
  const handleCreateVA = async () => {
    if (!selectedInvoice) return;
    
    const token = localStorage.getItem('customer_token');
    setPaymentLoading(selectedInvoice.id);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/xendit/fixed-va/create?invoice_id=${selectedInvoice.id}&bank_code=${selectedBank}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setVaResult(response.data.data);
        toast.success('Virtual Account berhasil dibuat!');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat Virtual Account');
    } finally {
      setPaymentLoading(null);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Nomor VA berhasil disalin!');
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('customer_token');
    setDownloadingInvoice(invoiceId);

    try {
      // Get invoice print data
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/customer/invoice/${invoiceId}/print-data`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const { invoice, customer: customerData, company_settings } = response.data.data;
        
        // Open new window for printing
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Mohon izinkan popup untuk download invoice');
          return;
        }

        // Generate print HTML
        const printHTML = generatePrintHTML(invoice, customerData, company_settings);
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for content to load then trigger print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };

        toast.success('Invoice siap di-download');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Gagal download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleDownloadThermal = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('customer_token');
    
    try {
      toast.loading('Membuat file thermal...', { id: 'thermal' });
      
      // Get thermal format from backend
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/customer/invoice/${invoiceId}/thermal`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const thermalText = response.data.data.thermal_text;
        
        // Create blob and download as .txt file
        const blob = new Blob([thermalText], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}_thermal.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('File berhasil didownload!\n\nBuka dengan aplikasi: Bluetooth Printer\nAtur font size: LARGE atau 2X', { 
          id: 'thermal',
          duration: 6000
        });
      }
    } catch (error) {
      console.error('Thermal download error:', error);
      toast.error('Gagal download thermal', { id: 'thermal' });
    }
  };

  const generatePrintHTML = (invoice, customerData, companySettings) => {
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('id-ID').format(amount || 0);
    };

    // Get bank accounts from company settings
    const bankAccounts = companySettings?.bank_accounts || [];

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    @page {
      size: A4;
      margin: 10mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      background: white;
    }
    .invoice-container {
      width: 190mm;
      margin: 0 auto;
      padding: 10mm;
      background: white;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
    }
    .company-logo {
      max-height: 64px;
      height: auto;
      margin-bottom: 8px;
    }
    .company-details {
      font-size: 12px;
      color: #4b5563;
      line-height: 1.6;
    }
    .invoice-title {
      text-align: right;
    }
    .invoice-title h1 {
      font-size: 28px;
      font-weight: bold;
      color: #312e81;
      margin-bottom: 8px;
    }
    .invoice-number {
      font-size: 14px;
      color: #4b5563;
      margin-bottom: 8px;
    }
    .status-badge {
      display: inline-block;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: bold;
    }
    .status-lunas {
      background: #22c55e;
      color: white;
    }
    .status-belum {
      background: #3b82f6;
      color: white;
    }
    .separator {
      border-bottom: 2px solid #4f46e5;
      margin: 20px 0;
    }
    .info-section {
      display: flex;
      justify-content: space-between;
      margin: 24px 0;
      gap: 40px;
    }
    .info-box {
      flex: 1;
    }
    .info-title {
      font-size: 14px;
      font-weight: bold;
      color: #312e81;
      margin-bottom: 12px;
    }
    .info-item {
      font-size: 12px;
      margin-bottom: 4px;
      color: #1f2937;
    }
    .info-label {
      color: #4b5563;
    }
    .info-value {
      font-weight: 600;
      color: #1f2937;
    }
    .items-section {
      margin: 24px 0;
    }
    .section-title {
      font-size: 14px;
      font-weight: bold;
      color: #312e81;
      margin-bottom: 12px;
    }
    .items-table {
      width: 100%;
      border-collapse: collapse;
    }
    .items-table thead {
      background: #f3f4f6;
      border-bottom: 2px solid #4f46e5;
    }
    .items-table th {
      padding: 12px;
      text-align: left;
      font-size: 12px;
      font-weight: bold;
      color: #1f2937;
    }
    .items-table td {
      padding: 12px;
      font-size: 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    .text-right {
      text-align: right;
    }
    .total-section {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
    }
    .total-box {
      width: 288px;
      background: #f9fafb;
      padding: 16px;
      border-radius: 4px;
      border: 1px solid #d1d5db;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 12px;
      color: #374151;
    }
    .total-row .value {
      font-weight: 600;
      color: #1f2937;
    }
    .total-final {
      display: flex;
      justify-content: space-between;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 2px solid #4f46e5;
      font-size: 16px;
      font-weight: bold;
      color: #312e81;
    }
    .bank-section {
      margin: 24px 0;
    }
    .bank-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .bank-box {
      background: #bbf7d0;
      color: #14532d;
      padding: 8px 12px;
      border-radius: 4px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .bank-name {
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 2px;
    }
    .bank-account-name {
      font-size: 10px;
      color: #166534;
      margin-bottom: 2px;
    }
    .bank-account-number {
      font-size: 11px;
      font-weight: bold;
      font-family: monospace;
    }
    .footer {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #d1d5db;
      text-align: center;
    }
    .footer-text {
      font-size: 12px;
      color: #4b5563;
      margin-bottom: 8px;
    }
    .footer-thanks {
      font-size: 14px;
      font-weight: bold;
      color: #312e81;
      margin-bottom: 8px;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .invoice-container {
        margin: 0;
        padding: 0;
      }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <div>
        <img src="${process.env.REACT_APP_BACKEND_URL}" alt="Company Logo" class="company-logo" onerror="this.style.display='none'" />
        <div class="company-details">
          ${companySettings.company_address || 'Telaga Golf Cluster Belanda Blok E10.61 Sawangan Depok'}<br>
          Telp: ${companySettings.company_phone || '08888200888'}
        </div>
      </div>
      <div class="invoice-title">
        <h1>INVOICE</h1>
        <div class="invoice-number">${invoice.invoice_number}</div>
        <span class="status-badge ${invoice.is_paid ? 'status-lunas' : 'status-belum'}">
          ${invoice.is_paid ? 'LUNAS' : 'BELUM BAYAR'}
        </span>
      </div>
    </div>

    <div class="separator"></div>

    <div class="info-section">
      <div class="info-box">
        <div class="info-title">DITAGIHKAN KE:</div>
        <div class="info-item">
          <span class="info-label">ID: </span>
          <span class="info-value">${customerData.account_number}</span>
        </div>
        <div class="info-item info-value" style="font-size: 14px; margin-top: 4px;">
          ${customerData.name}
        </div>
        <div class="info-item">
          ${customerData.address || '-'}
        </div>
        <div class="info-item">
          <span class="info-label">HP: </span>
          <span>${customerData.phone}</span>
        </div>
      </div>

      <div class="info-box">
        <div class="info-title">INFO INVOICE:</div>
        <div class="info-item">
          <span class="info-label">Tanggal: </span>
          <span class="info-value">${formatDate(invoice.created_at)}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Jatuh Tempo: </span>
          <span class="info-value">${formatDate(invoice.display_due_date || invoice.due_date)}</span>
        </div>
        ${invoice.payment_date ? `
        <div class="info-item">
          <span class="info-label">Tgl Bayar: </span>
          <span class="info-value">${formatDate(invoice.payment_date)}</span>
        </div>
        ` : ''}
      </div>
    </div>

    <div class="items-section">
      <div class="section-title">RINGKASAN LAYANAN</div>
      <table class="items-table">
        <thead>
          <tr>
            <th>Paket Langganan</th>
            <th>Jatuh Tempo</th>
            <th>Periode</th>
            <th class="text-right">Jumlah</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="color: #1f2937;">${customerData.service_plan || 'Layanan Internet'}</td>
            <td style="color: #4b5563;">${formatDate(invoice.display_due_date || invoice.due_date)}</td>
            <td style="color: #4b5563;">30 Hari</td>
            <td class="text-right" style="font-weight: bold; color: #1f2937;">Rp ${formatCurrency(invoice.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="total-section">
      <div class="total-box">
        <div class="total-row">
          <span>Subtotal:</span>
          <span class="value">Rp ${formatCurrency(invoice.subtotal || invoice.total)}</span>
        </div>
        <div class="total-row">
          <span>PPN (${invoice.tax_percentage || 0}%):</span>
          <span class="value">Rp ${formatCurrency(invoice.tax_amount || 0)}</span>
        </div>
        <div class="total-final">
          <span>Total:</span>
          <span>Rp ${formatCurrency(invoice.total)}</span>
        </div>
      </div>
    </div>

    ${invoice.is_paid ? `
    <!-- Transaction Table for LUNAS -->
    <div class="bank-section">
      <table style="width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #d1d5db;">
        <thead style="background: #f3f4f6;">
          <tr>
            <th style="border: 1px solid #d1d5db; padding: 8px; text-align: left;">Transaction Date</th>
            <th style="border: 1px solid #d1d5db; padding: 8px; text-align: left;">Gateway</th>
            <th style="border: 1px solid #d1d5db; padding: 8px; text-align: left;">Transaction ID</th>
            <th style="border: 1px solid #d1d5db; padding: 8px; text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid #d1d5db; padding: 8px;">${invoice.payment_date ? formatDate(invoice.payment_date) : '-'}</td>
            <td style="border: 1px solid #d1d5db; padding: 8px;">Manual</td>
            <td style="border: 1px solid #d1d5db; padding: 8px;">${invoice.invoice_number}</td>
            <td style="border: 1px solid #d1d5db; padding: 8px; text-align: right;">Rp. ${formatCurrency(invoice.total)}</td>
          </tr>
        </tbody>
      </table>
      <div style="margin-top: 12px; font-size: 12px;">
        <ul style="list-style: disc; padding-left: 20px;">
          <li>Pembayaran sudah diterima, terima kasih sudah melunasi tagihan anda</li>
          <li>Jika informasi pada bukti pembayaran ini ada kesalahan, silahkan hubungi kami</li>
        </ul>
      </div>
    </div>
    ` : bankAccounts.length > 0 ? `
    <!-- Bank Transfer for BELUM BAYAR -->
    <div class="bank-section">
      <div class="section-title">Transfer Manual</div>
      <ul style="list-style: disc; padding-left: 20px; font-size: 12px; line-height: 1.8;">
        ${bankAccounts.map(bank => `
        <li>
          <span style="color: #2563eb; font-weight: 600;">${bank.bank_name}</span>
          <span style="margin: 0 4px;">•</span>
          <span style="color: #16a34a; font-weight: 600;">${bank.account_name}</span>
          <span style="margin: 0 4px;">•</span>
          <span style="font-family: monospace; font-weight: 600;">${bank.account_number}</span>
        </li>
        `).join('')}
      </ul>
    </div>
    
    <!-- Pembayaran Tunai for BELUM BAYAR -->
    <div style="margin-bottom: 24px; font-size: 12px;">
      <div style="font-weight: bold; margin-bottom: 8px;">Pembayaran Tunai</div>
      <div>
        <p style="font-weight: 600; margin-bottom: 4px;">Layanan Internet</p>
        <p style="margin-bottom: 2px;">${companySettings.company_address || 'Telaga Golf Cluster Belanda Blok E10.61 Sawangan Depok'}</p>
        <p>${companySettings.company_phone || '08888200888'}</p>
      </div>
    </div>
    ` : ''}

    <div class="footer">
      <div class="footer-thanks">
        Terima Kasih Atas Kepercayaan Anda
      </div>
      <div style="font-size: 12px; color: #6b7280; font-style: italic; margin-top: 8px;">
        NOTE: This is computer generated receipt and does not require physical signature.
      </div>
    </div>
  </div>
</body>
</html>
    `;
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    navigate('/customer-login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Password baru tidak cocok');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('customer_token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2/customer/change-password`,
        null,
        {
          params: {
            current_password: passwordForm.currentPassword,
            new_password: passwordForm.newPassword
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password berhasil diubah!');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const unpaidInvoices = invoices.filter(inv => !inv.is_paid);
  const paidInvoices = invoices.filter(inv => inv.is_paid);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400 border-b border-orange-300 shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="" 
                alt="Logo" 
                className="h-14 w-auto object-contain"
              />
              <div className="h-8 w-px bg-white/50"></div>
              <h1 className="text-xl font-bold text-white drop-shadow">Portal Pelanggan</h1>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white/20 border border-white/30"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Keluar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Payment Alert Banner - Show when customer has unpaid invoices */}
        {unpaidInvoices.length > 0 && (
          <div 
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-5 rounded-xl mb-6 shadow-lg cursor-pointer hover:shadow-xl transition-all transform hover:-translate-y-0.5"
            onClick={() => {
              setActiveTab('billing');
              // Scroll to unpaid invoices section
              setTimeout(() => {
                const unpaidSection = document.getElementById('unpaid-invoices-section');
                if (unpaidSection) {
                  unpaidSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            data-testid="payment-alert-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-1">⚠️ Anda Memiliki Tagihan Belum Dibayar!</h2>
                  <p className="text-sm opacity-90">
                    Total: <span className="font-bold text-lg">Rp {dashboardStats?.total_unpaid?.toLocaleString('id-ID') || '0'}</span> 
                    <span className="mx-2">•</span>
                    {unpaidInvoices.length} Invoice belum lunas
                  </p>
                  <p className="text-xs opacity-80 mt-1">
                    Klik banner ini untuk melihat tagihan dan melakukan pembayaran
                  </p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-2 bg-white/20 rounded-lg px-4 py-2">
                <span className="text-sm font-semibold">BAYAR SEKARANG</span>
                <CreditCard className="w-5 h-5" />
              </div>
            </div>
          </div>
        )}

        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 p-6 rounded-lg mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2 dark:text-slate-100">
            ℹ️ Selamat Datang di Portal Pelanggan
          </h2>
          <p className="text-sm text-gray-700 dark:text-slate-200">
            Anda dapat menggunakan portal ini untuk: <strong>Pembayaran Tagihan, Cetak Invoice, Kirim Tiket</strong>, serta <strong>Cek Status Jatuh Tempo</strong>.
          </p>
          <p className="text-sm text-gray-600 mt-3 dark:text-slate-300">
            <strong>Catatan:</strong> Harap segera bayarkan tagihan anda sebelum jatuh tempo atau setelah invoice tagihan anda diterbitkan untuk menghindari penangguhan layanan internet (Tersolir)
          </p>
        </div>


        {/* Dashboard Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Unpaid Invoices Card */}
            <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-5 hover:shadow-md transition dark:bg-slate-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center dark:bg-orange-900/40">
                  <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium dark:text-slate-400">Tagihan Belum Dibayar</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{dashboardStats.unpaid_count}</p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400">Total Tagihan</p>
                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  Rp {dashboardStats.total_unpaid?.toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {/* Total Amount Card */}
            <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-5 hover:shadow-md transition dark:bg-slate-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center dark:bg-blue-900/40">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium dark:text-slate-400">Jatuh Tempo Terdekat</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {dashboardStats.next_due_date 
                      ? new Date(dashboardStats.next_due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })
                      : '-'
                    }
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400">Jumlah</p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  Rp {dashboardStats.next_due_amount?.toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {/* Service Plan Card */}
            <div className="bg-white rounded-xl shadow-sm border border-green-200 p-5 hover:shadow-md transition dark:bg-slate-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center dark:bg-green-900/40">
                  <Wifi className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium dark:text-slate-400">Paket Layanan</p>
                  <p className="text-base font-bold text-gray-900 dark:text-slate-100">{dashboardStats.service_plan}</p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400">Biaya Bulanan</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  Rp {dashboardStats.monthly_fee?.toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {/* Time Remaining Card */}
            <div className="bg-white rounded-xl shadow-sm border border-purple-200 p-5 hover:shadow-md transition dark:bg-slate-800">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center dark:bg-purple-900/40">
                  <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 font-medium dark:text-slate-400">Sisa Waktu Billing</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    {dashboardStats.days_remaining || 0} <span className="text-base font-normal text-gray-500 dark:text-slate-400">hari</span>
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400">Riwayat Pembayaran</p>
                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                  {dashboardStats.paid_count} Invoice
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Customer Info Card */}
        <div className="bg-gradient-to-br from-white to-orange-50 rounded-xl shadow-md border border-orange-100 p-4 sm:p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center dark:bg-orange-900/40">
              <User className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Informasi Akun</h2>
          </div>
          {/* Customer Details - 2 cols on mobile, 4 cols on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">ID Pelanggan</p>
              <p className="text-sm font-semibold text-gray-900 break-all dark:text-slate-100">{customer?.account_number}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Nama</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">{customer?.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Status Pembayaran</p>
              <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${
                (dashboardStats?.payment_status_computed === 'LUNAS' || customer?.payment_status_computed === 'LUNAS' || unpaidInvoices.length === 0)
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {(dashboardStats?.payment_status_computed === 'LUNAS' || customer?.payment_status_computed === 'LUNAS' || unpaidInvoices.length === 0) ? '✓ LUNAS' : '✗ BELUM BAYAR'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 dark:text-slate-400">Status Koneksi</p>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                dashboardStats?.is_online
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}>
                <span className={`w-2 h-2 rounded-full ${dashboardStats?.is_online ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
                {dashboardStats?.is_online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button
              onClick={() => setShowChangePassword(true)}
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/30"
            >
              <Key className="w-4 h-4 mr-2" />
              Ubah Password
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{backgroundColor: '#1e293b', padding: '8px', borderRadius: '12px', marginBottom: '24px'}}>
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('billing')}
              style={{
                flex: 1,
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s',
                backgroundColor: activeTab === 'billing' ? '#f97316' : '#334155',
                color: activeTab === 'billing' ? 'white' : '#cbd5e1'
              }}
            >
              <CreditCard className="w-5 h-5" />
              Tagihan & Pembayaran
            </button>
            <button
              onClick={() => setActiveTab('wifi')}
              disabled={!dashboardStats?.customer?.wan_device_id}
              title={!dashboardStats?.customer?.wan_device_id ? 'Perangkat ONT tidak ditemukan' : 'Kelola pengaturan WiFi'}
              style={{
                flex: 1,
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                border: 'none',
                cursor: !dashboardStats?.customer?.wan_device_id ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                backgroundColor: activeTab === 'wifi' ? '#3b82f6' : '#334155',
                color: activeTab === 'wifi' ? 'white' : '#cbd5e1',
                opacity: !dashboardStats?.customer?.wan_device_id ? 0.5 : 1
              }}
            >
              <Wifi className="w-5 h-5" />
              Pengaturan WiFi
              {!dashboardStats?.customer?.wan_device_id && (
                <span style={{fontSize: '12px', color: '#94a3b8', marginLeft: '4px'}}>(N/A)</span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'billing' ? (
          <>
            {/* Unpaid Invoices - Compact */}
            {unpaidInvoices.length > 0 && (
              <div className="mb-6" id="unpaid-invoices-section" data-testid="unpaid-invoices-section">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center dark:text-slate-100">
                  <Clock className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400" />
                  Tagihan Belum Dibayar ({unpaidInvoices.length})
                </h2>
                <div className="space-y-2">
                  {unpaidInvoices.map((invoice) => (
                <div key={invoice.id} className="bg-white rounded-lg shadow-sm border-l-4 border-orange-500 border border-gray-200 p-4 hover:shadow-md transition dark:bg-slate-800 dark:border-slate-700">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <FileText className="w-4 h-4 text-orange-600 flex-shrink-0 dark:text-orange-400" />
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-slate-100">
                          {invoice.invoice_number}
                        </h3>
                        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium dark:bg-orange-900/40 dark:text-orange-300">
                          {invoice.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div>
                          <p className="text-gray-500 mb-0.5 dark:text-slate-400">Tanggal</p>
                          <p className="font-medium text-gray-900 dark:text-slate-100">
                            {new Date(invoice.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5 dark:text-slate-400">Jatuh Tempo</p>
                          <p className="font-medium text-red-600 dark:text-red-400">
                            {new Date(invoice.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5 dark:text-slate-400">Total</p>
                          <p className="font-bold text-sm text-gray-900 dark:text-slate-100">
                            Rp {invoice.total?.toLocaleString('id-ID')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                        disabled={downloadingInvoice === invoice.id}
                        variant="outline"
                        size="sm"
                        className="text-gray-700 border-gray-300 hover:bg-gray-50 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700/50"
                        title="Download PDF (A4)"
                      >
                        {downloadingInvoice === invoice.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDownloadThermal(invoice.id, invoice.invoice_number)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                        title="Download Thermal 58mm (RPP02)"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handlePayment(invoice)}
                        disabled={paymentLoading === invoice.id}
                        size="sm"
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold shadow-sm"
                      >
                        {paymentLoading === invoice.id ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            <span className="text-xs">Memproses...</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 mr-1.5" />
                            <span className="text-xs">BAYAR</span>
                          </div>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Paid Invoices - Compact Table */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center dark:bg-green-900/40">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Riwayat Pembayaran ({paidInvoices.length})</h2>
          </div>
          {paidInvoices.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center dark:bg-slate-800 dark:border-slate-700">
              <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-slate-400">Belum ada riwayat pembayaran</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden dark:bg-slate-800 dark:border-slate-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">No. Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">Tanggal</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">Jatuh Tempo</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider dark:text-slate-300">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                    {paidInvoices.map((invoice, index) => (
                      <tr key={invoice.id} className="hover:bg-gray-50 transition dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 dark:text-green-400" />
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">{invoice.invoice_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-slate-300">
                          {new Date(invoice.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-slate-300">
                          {new Date(invoice.display_due_date || invoice.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-slate-100">
                          Rp {invoice.total?.toLocaleString('id-ID')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40">
                            LUNAS
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                              disabled={downloadingInvoice === invoice.id}
                              variant="ghost"
                              size="sm"
                              className="text-green-700 hover:bg-green-50 h-8 px-3 dark:text-green-300 dark:hover:bg-green-900/30"
                              title="Download PDF (A4)"
                            >
                              {downloadingInvoice === invoice.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              onClick={() => handleDownloadThermal(invoice.id, invoice.invoice_number)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:bg-blue-50 h-8 px-3 dark:text-blue-400 dark:hover:bg-blue-900/30"
                              title="Download Thermal 58mm (RPP02)"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          </>
        ) : (
          /* WiFi Management Tab */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 dark:bg-slate-800 dark:border-slate-700">
            {dashboardStats?.customer?.wan_device_id ? (
              <CustomerWiFiManagement 
                customer={customer} 
                deviceId={dashboardStats?.customer?.wan_device_id} 
              />
            ) : (
              <div className="text-center py-12">
                <Wifi className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200">Perangkat ONT Tidak Ditemukan</h3>
                <p className="text-slate-500 mt-2 dark:text-slate-400">
                  Akun Anda belum terhubung dengan perangkat ONT/Router.<br/>
                  Silakan hubungi customer service untuk bantuan.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              <span>Ubah Password</span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Password Saat Ini
              </label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                placeholder="Masukkan password saat ini"
                required
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Password Baru
              </label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                placeholder="Masukkan password baru"
                required
                minLength={6}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                Konfirmasi Password Baru
              </label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                placeholder="Konfirmasi password baru"
                required
                minLength={6}
                className="w-full"
              />
            </div>
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="flex-1"
                disabled={passwordLoading}
              >
                Batal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Menyimpan...
                  </div>
                ) : (
                  'Ubah Password'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal - Select Bank & Create VA */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Pembayaran Virtual Account
            </DialogTitle>
          </DialogHeader>
          
          {!vaResult ? (
            <div className="space-y-4">
              {/* Invoice Info */}
              {selectedInvoice && (
                <div className="bg-gray-50 rounded-lg p-4 dark:bg-slate-900">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600 dark:text-slate-300">No. Invoice</span>
                    <span className="font-medium">{selectedInvoice.invoice_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-slate-300">Total Tagihan</span>
                    <span className="font-bold text-lg text-blue-600 dark:text-blue-400">
                      {formatCurrency(selectedInvoice.total)}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Bank Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block dark:text-slate-200">
                  Pilih Bank untuk Virtual Account
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availableBanks.map((bank) => (
                    <button
                      key={bank.code}
                      onClick={() => setSelectedBank(bank.code)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        selectedBank === bank.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 ${bank.color} rounded flex items-center justify-center`}>
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium">{bank.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Create VA Button */}
              <Button
                onClick={handleCreateVA}
                disabled={paymentLoading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {paymentLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Membuat Virtual Account...
                  </span>
                ) : (
                  'Buat Virtual Account'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* VA Result */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
                <div className="flex items-center gap-2 text-green-700 mb-3 dark:text-green-300">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Virtual Account Berhasil Dibuat!</span>
                </div>
                
                {/* VA Name */}
                <div className="bg-white rounded-lg p-3 mb-3 dark:bg-slate-800">
                  <div className="text-xs text-gray-500 mb-1 dark:text-slate-400">Nama Virtual Account</div>
                  <div className="font-semibold text-gray-800 dark:text-slate-200">{vaResult.va_name}</div>
                </div>
                
                {/* VA Number */}
                <div className="bg-white rounded-lg p-3 mb-3 dark:bg-slate-800">
                  <div className="text-xs text-gray-500 mb-1 dark:text-slate-400">Nomor Virtual Account ({vaResult.va_bank})</div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xl font-bold text-blue-600 dark:text-blue-400">{vaResult.va_number}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(vaResult.va_number)}
                      className="text-xs"
                    >
                      Salin
                    </Button>
                  </div>
                </div>
                
                {/* Amount */}
                <div className="bg-white rounded-lg p-3 mb-3 dark:bg-slate-800">
                  <div className="text-xs text-gray-500 mb-1 dark:text-slate-400">Jumlah yang harus dibayar</div>
                  <div className="font-bold text-xl text-gray-800 dark:text-slate-200">{formatCurrency(vaResult.amount)}</div>
                </div>
                
                {/* Expiry */}
                <div className="text-xs text-gray-500 text-center dark:text-slate-400">
                  Berlaku hingga: {new Date(vaResult.expires_at).toLocaleString('id-ID')}
                </div>
              </div>
              
              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-4 dark:bg-blue-900/30">
                <div className="text-sm font-medium text-blue-800 mb-2">Cara Pembayaran:</div>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside dark:text-blue-300">
                  <li>Buka aplikasi m-Banking atau ATM</li>
                  <li>Pilih menu Transfer ke Virtual Account</li>
                  <li>Masukkan nomor VA di atas</li>
                  <li>Konfirmasi nama dan jumlah pembayaran</li>
                  <li>Selesaikan pembayaran</li>
                </ol>
              </div>
              
              <Button
                onClick={() => {
                  setShowPaymentModal(false);
                  setVaResult(null);
                  fetchInvoices(localStorage.getItem('customer_token'));
                }}
                variant="outline"
                className="w-full"
              >
                Tutup
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
